import { useMemo, useState, useCallback } from 'react';
import { Event } from '../types/event.types';

export interface CalendarDay {
  date: Date;
  dayNumber: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  eventCount: number;
}

export interface UseDayPickerReturn {
  isOpen: boolean;
  toggleOpen: () => void;
  close: () => void;

  displayedMonth: Date;
  goToNextMonth: () => void;
  goToPreviousMonth: () => void;

  calendarDays: CalendarDay[];

  closedLabel: string;
  openLabel: string;
}

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * Format: "Wednesday 5 March 2026"
 */
const formatClosedLabel = (date: Date): string => {
  const dayName = DAY_NAMES[date.getDay() === 0 ? 6 : date.getDay() - 1];
  const dayNum = date.getDate();
  const month = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();
  return `${dayName} ${dayNum} ${month} ${year}`;
};

/**
 * Format: "March 2026"
 */
const formatOpenLabel = (date: Date): string => {
  return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * Build a day key "YYYY-MM-DD" for event counting
 */
const toDayKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Check if two dates are the same calendar day
 */
const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * useDayPicker — Manages the collapsible date picker state
 */
export const useDayPicker = (
  selectedDate: Date,
  events: Event[]
): UseDayPickerReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayedMonth, setDisplayedMonth] = useState<Date>(() => {
    return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
  });

  const toggleOpen = useCallback(() => {
    setIsOpen(prev => {
      if (!prev) {
        // When opening, sync displayed month to selected date
        setDisplayedMonth(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
      }
      return !prev;
    });
  }, [selectedDate]);

  const close = useCallback(() => setIsOpen(false), []);

  const goToNextMonth = useCallback(() => {
    setDisplayedMonth(prev => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + 1);
      return next;
    });
  }, []);

  const goToPreviousMonth = useCallback(() => {
    setDisplayedMonth(prev => {
      const prev2 = new Date(prev);
      prev2.setMonth(prev2.getMonth() - 1);
      return prev2;
    });
  }, []);

  // Count events per day for the displayed month
  const eventCountByDay = useMemo(() => {
    const counts = new Map<string, number>();
    for (const event of events) {
      if (!event.datetime) continue;
      const key = toDayKey(new Date(event.datetime));
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }, [events]);

  const calendarDays = useMemo((): CalendarDay[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const year = displayedMonth.getFullYear();
    const month = displayedMonth.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Day of week (0=Sun, 1=Mon, ..., 6=Sat) → convert to Mon=0
    let startDow = firstDay.getDay() - 1;
    if (startDow < 0) startDow = 6; // Sunday → 6

    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    const days: CalendarDay[] = [];

    // Previous month padding days
    for (let i = startDow - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({
        date,
        dayNumber: date.getDate(),
        isCurrentMonth: false,
        isToday: isSameDay(date, today),
        isSelected: isSameDay(date, selectedDate),
        eventCount: eventCountByDay.get(toDayKey(date)) || 0,
      });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      days.push({
        date,
        dayNumber: d,
        isCurrentMonth: true,
        isToday: isSameDay(date, today),
        isSelected: isSameDay(date, selectedDate),
        eventCount: eventCountByDay.get(toDayKey(date)) || 0,
      });
    }

    // Next month padding days (fill to complete the last week row)
    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        const date = new Date(year, month + 1, i);
        days.push({
          date,
          dayNumber: i,
          isCurrentMonth: false,
          isToday: isSameDay(date, today),
          isSelected: isSameDay(date, selectedDate),
          eventCount: eventCountByDay.get(toDayKey(date)) || 0,
        });
      }
    }

    return days;
  }, [displayedMonth, selectedDate, eventCountByDay]);

  const closedLabel = useMemo(() => formatClosedLabel(selectedDate), [selectedDate]);
  const openLabel = useMemo(() => formatOpenLabel(displayedMonth), [displayedMonth]);

  return {
    isOpen,
    toggleOpen,
    close,
    displayedMonth,
    goToNextMonth,
    goToPreviousMonth,
    calendarDays,
    closedLabel,
    openLabel,
  };
};
